// GA4 analytics helpers. The gtag.js tag is loaded in index.html
// (measurement id G-2K26LFR0L0), so window.gtag exists at runtime. Every call
// is window-guarded + optional-chained, so it safely no-ops if GA hasn't
// loaded yet (or during SSR/prerender).

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

const GA_MEASUREMENT_ID = 'G-2K26LFR0L0'

type SignupMethod = 'email' | 'google'

/**
 * Fire the GA4 `sign_up` event exactly once, on real account creation.
 *
 * Call ONLY when a brand-new account is created — never on plain login. This
 * is the single clean signup signal: mark `sign_up` as a Key event in GA4,
 * import it into Google Ads, and make it the SOLE primary conversion. It
 * replaces the old page-load-based conversions ("Sign-up Dashboard" fired on
 * every /dashboard load; "Sign-up" fired on every /ai-for-small-business
 * landing-page view), which over-counted logins and ad clicks as signups.
 */
export function trackSignup(method: SignupMethod): void {
  if (typeof window === 'undefined') return
  window.gtag?.('event', 'sign_up', { method })
}

/**
 * Activation — fires `first_agent_message` once per browser the first time a
 * user sends a message to an agent. The key SaaS funnel metric: of everyone
 * who signs up, who actually engages with the product.
 */
export function trackActivation(): void {
  if (typeof window === 'undefined') return
  try {
    if (localStorage.getItem('aspired_activated')) return
    localStorage.setItem('aspired_activated', '1')
  } catch {
    // localStorage blocked — fall through and fire anyway.
  }
  window.gtag?.('event', 'first_agent_message')
}

/** GA4 recommended `begin_checkout` — lets us measure checkout abandonment. */
export function trackBeginCheckout(valueDollars: number, planSlug: string): void {
  if (typeof window === 'undefined') return
  window.gtag?.('event', 'begin_checkout', {
    value: valueDollars,
    currency: 'USD',
    items: [{ item_id: planSlug, item_name: planSlug }],
  })
}

/**
 * Enhanced conversions — hand GA4 the user's email so it can match conversions
 * to Google Ads clicks more accurately. GA4 hashes it client-side before send.
 * Call once the user's email is known (signup/login), before firing conversions.
 * Requires "user-provided data collection" enabled in the GA4 data stream.
 */
export function setUserData(email?: string | null): void {
  if (typeof window === 'undefined' || !email) return
  window.gtag?.('set', 'user_data', { email })
}

/**
 * Read the GA4 client_id + session_id from gtag so the backend can send a
 * server-side `purchase` event (Measurement Protocol) that's attributed to
 * THIS user's ad-click session. Called at checkout and passed through to the
 * Stripe subscription metadata. Resolves {} if gtag isn't ready (never rejects).
 */
export function getGaIds(): Promise<{ ga_client_id?: string; ga_session_id?: string }> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.gtag) {
      resolve({})
      return
    }
    const out: { ga_client_id?: string; ga_session_id?: string } = {}
    let pending = 2
    const done = () => {
      pending -= 1
      if (pending === 0) resolve(out)
    }
    try {
      window.gtag('get', GA_MEASUREMENT_ID, 'client_id', (v: string) => {
        if (v) out.ga_client_id = v
        done()
      })
      window.gtag('get', GA_MEASUREMENT_ID, 'session_id', (v: unknown) => {
        if (v) out.ga_session_id = String(v)
        done()
      })
    } catch {
      resolve(out)
    }
    // Safety net if a gtag callback never fires.
    setTimeout(() => resolve(out), 800)
  })
}
